import type { PlanningRow } from '@crm/shared'
import { PlanningAmountInput } from './PlanningAmountInput'
import {
  PLAN_READONLY_TITLE,
  ROLE_LABELS,
  formatFactDate,
  percent,
  percentColorClass,
} from './planningFormat'

interface PlanningRowViewProps {
  row: PlanningRow
  isSelected: boolean
  /** Колонка чекбокса выбора для графика дашборда (у помощника её нет). */
  showSelection?: boolean
  /** Строка только для чтения: ГД, помощник, свой план РОПа, сумма за несколько месяцев. */
  readonly?: boolean
  readonlyTitle?: string
  /** Только план — для чтения (бухгалтер вносит только факт). */
  planReadonly?: boolean
  onToggle: () => void
  /** true — сохранено, false — сервер отказал (тост уже показан). */
  onPatch: (planAmount?: number, factAmount?: number) => Promise<boolean>
}

function formatRub(n: number): string {
  return n.toLocaleString('ru-RU')
}

/** Строка финансовой таблицы «Планирования»: план и факт в ₽ с копейками. */
export function PlanningRowView({
  row,
  isSelected,
  showSelection = true,
  readonly = false,
  readonlyTitle,
  planReadonly = false,
  onToggle,
  onPatch,
}: PlanningRowViewProps) {
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      {showSelection && (
        <td className="px-3 py-3 text-center align-top">
          <input type="checkbox" checked={isSelected} onChange={onToggle} className="mt-1.5" />
        </td>
      )}
      <td className="px-3 py-3 align-top">
        <div className="font-medium text-gray-900">{row.managerName}</div>
        <div className="text-xs text-gray-400">
          {ROLE_LABELS[row.role] ?? row.role} · {row.managerEmail}
        </div>
      </td>
      <td className="px-3 py-3 text-right align-top">
        <PlanningAmountInput
          value={row.planAmount}
          kind="money"
          label={`План, ₽ — ${row.managerName}`}
          readonly={readonly || planReadonly}
          readonlyTitle={readonlyTitle ?? (planReadonly ? PLAN_READONLY_TITLE : undefined)}
          onSave={(v) => onPatch(v, undefined)}
        />
      </td>
      <td className="px-3 py-3 text-right align-top">
        <PlanningAmountInput
          value={row.factAmount}
          kind="money"
          label={`Факт, ₽ — ${row.managerName}`}
          readonly={readonly}
          readonlyTitle={readonlyTitle}
          onSave={(v) => onPatch(undefined, v)}
        />
        <div className="mt-1 text-[10px] text-gray-400 whitespace-nowrap">
          {formatFactDate(row.factUpdatedAt)}
        </div>
      </td>
      <td
        className={`px-3 py-3 pt-4 text-right tabular-nums align-top ${percentColorClass(row.planAmount, row.factAmount)}`}
      >
        {percent(row.planAmount, row.factAmount)}
      </td>
      <td className="px-3 py-3 pt-4 text-right tabular-nums align-top">
        {formatRub(row.quotedAmount)}
      </td>
      <td className="px-3 py-3 pt-4 text-right tabular-nums align-top">
        {formatRub(row.orderedAmount)}
      </td>
    </tr>
  )
}
